import type { MenuProps } from "antd";
import { Dropdown } from "antd";
import { useState } from "react";
import { toSentenceCase } from "~/common/converters";
import { Icon, iconPaintProps } from "../../Icons";
import { TimelineItemTypeConfig } from "../types";

interface IndicatorEditProps<T extends string> {
  /**
   * Current type value
   */
  type: T;

  /**
   * Type configuration mapping
   */
  typeConfig: Record<T, TimelineItemTypeConfig>;

  /**
   * Whether to show the vertical connector line
   */
  showConnector: boolean;

  /**
   * Callback when type changes
   */
  onTypeChange?: (type: T) => void;
}

/**
 * Indicator section of the TimelineItem in edit mode
 * Displays a dropdown to select the type with icon preview
 */
export const TimelineItemIndicatorEdit = <T extends string>({
  type,
  typeConfig,
  showConnector,
  onTypeChange,
}: IndicatorEditProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);

  const currentConfig = typeConfig[type];

  // Convert typeConfig to menu items with icons
  const menuItems: MenuProps["items"] = Object.keys(typeConfig).map((key) => {
    const config = typeConfig[key as T];
    return {
      key: key,
      label: (
        <div className="indigo-timeline-item__type-menu-item">
          <div
            className="indigo-timeline-item__type-menu-icon"
            style={{
              backgroundColor: config.bgColor,
            }}
          >
            <Icon {...iconPaintProps(config.icon, config.iconColor)} size="m" />
          </div>
          <span>{toSentenceCase(key)}</span>
        </div>
      ),
      onClick: () => {
        onTypeChange?.(key as T);
        setIsOpen(false);
      },
    };
  });

  return (
    <div className="indigo-timeline-item__indicator indigo-timeline-item__indicator--edit">
      <Dropdown
        menu={{ items: menuItems }}
        trigger={["click"]}
        open={isOpen}
        onOpenChange={setIsOpen}
        overlayClassName="indigo-timeline-item__type-dropdown-menu"
      >
        <button
          className="indigo-timeline-item__icon-button"
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          <div
            className="indigo-timeline-item__icon-container"
            style={{
              backgroundColor: currentConfig.bgColor,
            }}
            title={currentConfig.label}
          >
            <Icon
              {...iconPaintProps(currentConfig.icon, currentConfig.iconColor)}
              size="m"
              className="indigo-timeline-item__icon"
            />
          </div>
        </button>
      </Dropdown>
      {showConnector && <div className="indigo-timeline-item__connector" />}
    </div>
  );
};
